import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  addCustomerIdentity,
  createCustomer,
  decideCustomerMerge,
  listComplaints,
  listConsents,
  listCustomerIdentities,
  listCustomerMergeRequests,
  listCustomers,
  listFeedback,
  logComplaint,
  logFeedback,
  resolveComplaint,
  setConsent,
  updateComplaintStatus,
  updateCustomer,
  updateFeedbackStatus,
} from '../../api/crm';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  GroupLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type { Complaint, Consent, Customer, CustomerIdentity, CustomerIdentityType, CustomerMergeRequest, Feedback } from '../../types/domain';

const CONSENT_TYPES: Array<Consent['consent_type']> = ['MARKETING', 'WHATSAPP'];
const CONSENT_LABEL: Record<Consent['consent_type'], string> = { MARKETING: 'Marketing', WHATSAPP: 'WhatsApp' };

/**
 * CRM Foundation (FASE 11, migration 0048). Customer identity distinct from
 * ERP user/employee identity, tenant-wide, PII-sensitive. Merge is NEVER
 * automatic (V5 non-negotiable) — a conflicting identity only ever produces
 * a PENDING_REVIEW row here; resolving it requires an explicit human decide
 * action, server-enforced maker-checker. Transactional loyalty (points/
 * rewards/coupons) is explicitly DEFERRED_WITH_POS and has no UI anywhere
 * in this page — this is identity/consent/feedback/complaint only.
 */
export default function CrmPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [tab, setTab] = useState<'customers' | 'feedback' | 'complaints' | 'merges'>('customers');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [mergeRequests, setMergeRequests] = useState<CustomerMergeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const canCreateCustomer = hasPermission('customer', 'create');
  const canDecideMerge = hasPermission('customer_merge_request', 'decide');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, f, cx, m] = await Promise.all([listCustomers(), listFeedback(), listComplaints(), listCustomerMergeRequests()]);
      setCustomers(c);
      setFeedback(f);
      setComplaints(cx);
      setMergeRequests(m);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const customerById = useMemo(() => new Map(customers.map((c) => [c.customer_id, c])), [customers]);

  const filteredCustomers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return customers.filter((c) => {
      if (q && !c.name.toLowerCase().includes(q) && !c.customer_no.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [customers, search]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filteredCustomers, 20);

  const pendingMerges = mergeRequests.filter((m) => m.status === 'PENDING_REVIEW');
  const openComplaints = complaints.filter((c) => c.status !== 'RESOLVED').length;
  const newFeedback = feedback.filter((f) => f.status === 'NEW').length;

  return (
    <div>
      <PageHeader title="CRM" subtitle="Identitas pelanggan, consent, feedback, komplain. Migrasi 0048." />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <Button variant={tab === 'customers' ? 'primary' : 'secondary'} onClick={() => setTab('customers')}>
          Pelanggan
        </Button>
        <Button variant={tab === 'feedback' ? 'primary' : 'secondary'} onClick={() => setTab('feedback')}>
          Feedback ({newFeedback} baru)
        </Button>
        <Button variant={tab === 'complaints' ? 'primary' : 'secondary'} onClick={() => setTab('complaints')}>
          Komplain ({openComplaints} terbuka)
        </Button>
        {canDecideMerge && (
          <Button variant={tab === 'merges' ? 'primary' : 'secondary'} onClick={() => setTab('merges')}>
            Review Duplikasi ({pendingMerges.length})
          </Button>
        )}
      </div>

      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}

      {!loading && !error && tab === 'customers' && (
        <>
          {canCreateCustomer && (
            <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
              {showForm ? 'Batal' : '+ Pelanggan Baru'}
            </Button>
          )}
          {showForm && canCreateCustomer && activeBranchId && (
            <NewCustomerForm
              branchId={activeBranchId}
              onCreated={() => {
                setShowForm(false);
                void load();
              }}
            />
          )}
          <SummaryStrip
            items={[
              { label: 'Total Pelanggan', value: customers.length },
              { label: 'Aktif', value: customers.filter((c) => c.status === 'ACTIVE').length, tone: 'success' as const },
              { label: 'Digabung', value: customers.filter((c) => c.status === 'MERGED').length },
            ]}
          />
          <ListToolbar search={search} onSearchChange={setSearch} searchPlaceholder="Cari nama/no. pelanggan..." resultCount={total} />
          {customers.length === 0 && <EmptyState text="Belum ada pelanggan terdaftar." />}
          {customers.length > 0 && total === 0 && <EmptyState text="Tidak ada pelanggan yang cocok." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>No. Pelanggan</th>
                  <th style={thStyle}>Nama</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((c) => (
                  <ClickableRow key={c.customer_id} onClick={() => setSelectedCustomer(c)}>
                    <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{c.customer_no}</td>
                    <td style={tdStyle}>{c.name}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={c.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}

      {!loading && !error && tab === 'feedback' && (
        <FeedbackTab
          feedback={feedback}
          customerById={customerById}
          canUpdate={hasPermission('feedback', 'update')}
          onChanged={() => void load()}
        />
      )}

      {!loading && !error && tab === 'complaints' && (
        <ComplaintsTab
          complaints={complaints}
          customerById={customerById}
          canUpdate={hasPermission('complaint', 'update')}
          onChanged={() => void load()}
        />
      )}

      {!loading && !error && tab === 'merges' && canDecideMerge && (
        <MergeReviewTab mergeRequests={mergeRequests} customerById={customerById} onChanged={() => void load()} />
      )}

      <CustomerDetailDrawer
        customer={selectedCustomer}
        open={!!selectedCustomer}
        onClose={() => setSelectedCustomer(null)}
        branchId={activeBranchId}
        canUpdate={hasPermission('customer', 'update')}
        canUpdateConsent={hasPermission('consent', 'update')}
        canCreateFeedback={hasPermission('feedback', 'create')}
        canCreateComplaint={hasPermission('complaint', 'create')}
        onChanged={() => void load()}
      />
    </div>
  );
}

function NewCustomerForm({ branchId, onCreated }: { branchId: string; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createCustomer(branchId, name.trim());
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <FieldLabel label="Nama Pelanggan">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function CustomerDetailDrawer({
  customer,
  open,
  onClose,
  branchId,
  canUpdate,
  canUpdateConsent,
  canCreateFeedback,
  canCreateComplaint,
  onChanged,
}: {
  customer: Customer | null;
  open: boolean;
  onClose: () => void;
  branchId: string | null;
  canUpdate: boolean;
  canUpdateConsent: boolean;
  canCreateFeedback: boolean;
  canCreateComplaint: boolean;
  onChanged: () => void;
}) {
  const [identities, setIdentities] = useState<CustomerIdentity[]>([]);
  const [consents, setConsents] = useState<Consent[]>([]);
  const [identityType, setIdentityType] = useState<CustomerIdentityType>('PHONE');
  const [identityValue, setIdentityValue] = useState('');
  const [nameEdit, setNameEdit] = useState('');
  const [fbChannel, setFbChannel] = useState('WHATSAPP');
  const [fbRating, setFbRating] = useState('5');
  const [fbComment, setFbComment] = useState('');
  const [complaintSubject, setComplaintSubject] = useState('');
  const [complaintDesc, setComplaintDesc] = useState('');
  // Generated once per logical submission (form-open, then regenerated only
  // after a successful create) — a double-click, network retry, or slow
  // connection resubmit reuses the SAME key, so the server's `unique
  // (tenant_id, idempotency_key)` constraint resolves it to one canonical
  // row. Never regenerated per-keystroke or per-click.
  const [fbIdempotencyKey, setFbIdempotencyKey] = useState(() => crypto.randomUUID());
  const [complaintIdempotencyKey, setComplaintIdempotencyKey] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flagMsg, setFlagMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const reload = async (customerId: string) => {
    const [idn, cons] = await Promise.all([listCustomerIdentities(customerId), listConsents(customerId)]);
    setIdentities(idn);
    setConsents(cons);
  };

  useEffect(() => {
    if (!customer) return;
    setError(null);
    setFlagMsg(null);
    setOkMsg(null);
    setIdentityValue('');
    setNameEdit(customer.name);
    setFbComment('');
    setComplaintSubject('');
    setComplaintDesc('');
    setFbIdempotencyKey(crypto.randomUUID());
    setComplaintIdempotencyKey(crypto.randomUUID());
    void reload(customer.customer_id);
  }, [customer?.customer_id]);

  if (!customer) return null;

  const effectiveConsent = (type: Consent['consent_type']) => consents.filter((c) => c.consent_type === type).sort((a, b) => b.changed_at.localeCompare(a.changed_at))[0] ?? null;

  const handleAddIdentity = async () => {
    if (!identityValue.trim()) return;
    setBusy(true);
    setError(null);
    setFlagMsg(null);
    try {
      const mergeRequestId = await addCustomerIdentity(customer.customer_id, identityType, identityValue.trim());
      if (mergeRequestId) {
        setFlagMsg('Identitas ini sudah dimiliki pelanggan lain — ditandai untuk Review Duplikasi, tidak digabung otomatis.');
      }
      setIdentityValue('');
      await reload(customer.customer_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleToggleConsent = async (type: Consent['consent_type'], granted: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await setConsent(customer.customer_id, type, granted);
      await reload(customer.customer_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveName = async () => {
    if (!nameEdit.trim() || nameEdit.trim() === customer.name) return;
    setBusy(true);
    setError(null);
    try {
      await updateCustomer(customer.customer_id, nameEdit.trim(), customer.primary_branch_id);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleLogFeedback = async () => {
    const targetBranch = customer.primary_branch_id ?? branchId;
    if (!targetBranch) {
      setError('Pilih cabang aktif terlebih dahulu.');
      return;
    }
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      await logFeedback(targetBranch, customer.customer_id, fbChannel, fbRating ? Number(fbRating) : null, fbComment.trim() || null, fbIdempotencyKey);
      setFbComment('');
      setFbIdempotencyKey(crypto.randomUUID());
      setOkMsg('Feedback tercatat.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleLogComplaint = async () => {
    const targetBranch = customer.primary_branch_id ?? branchId;
    if (!targetBranch || !complaintSubject.trim() || !complaintDesc.trim()) {
      setError('Lengkapi subjek dan deskripsi komplain (dan pastikan cabang aktif terpilih).');
      return;
    }
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      await logComplaint(targetBranch, customer.customer_id, complaintSubject.trim(), complaintDesc.trim(), complaintIdempotencyKey);
      setComplaintSubject('');
      setComplaintDesc('');
      setComplaintIdempotencyKey(crypto.randomUUID());
      setOkMsg('Komplain tercatat — lihat di tab Komplain untuk proses lanjutan.');
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={`${customer.customer_no} — ${customer.name}`}>
      <DrawerField label="Status" value={<StatusBadge status={customer.status} />} />
      {customer.status === 'MERGED' && <DrawerField label="Catatan" value="Digabung ke pelanggan lain — riwayat tetap tersimpan, tidak dapat diedit di sini." />}
      {canUpdate && customer.status === 'ACTIVE' && (
        <div style={{ marginTop: 10 }}>
          <FieldLabel label="Nama Pelanggan">
            <div style={{ display: 'flex', gap: 8 }}>
              <TextInput value={nameEdit} onChange={(e) => setNameEdit(e.target.value)} style={{ flex: 1 }} />
              <Button variant="secondary" loading={busy} onClick={() => void handleSaveName()} disabled={nameEdit.trim() === customer.name}>
                Simpan
              </Button>
            </div>
          </FieldLabel>
        </div>
      )}
      {error && <ErrorNote text={error} />}
      {okMsg && <div className="alex-card" style={{ padding: 10, marginTop: 8, fontSize: 'var(--alex-fs-body)' }}>{okMsg}</div>}
      {flagMsg && <div className="alex-card" style={{ padding: 10, marginTop: 8, fontSize: 'var(--alex-fs-body)', background: 'var(--alex-warning-soft, #fff8e1)' }}>{flagMsg}</div>}

      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
        <GroupLabel>Identitas (Telepon/Email)</GroupLabel>
        {identities.length === 0 && <EmptyState text="Belum ada identitas tercatat." variant="section" />}
        {identities.map((idn) => (
          <div key={idn.identity_id} style={{ padding: '4px 0', fontSize: 'var(--alex-fs-body)' }}>
            {idn.identity_type === 'PHONE' ? 'Telepon' : 'Email'}: {idn.identity_value}
          </div>
        ))}
        {canUpdate && customer.status === 'ACTIVE' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <Select value={identityType} onChange={(e) => setIdentityType(e.target.value as CustomerIdentityType)} style={{ width: 110 }}>
              <option value="PHONE">Telepon</option>
              <option value="EMAIL">Email</option>
            </Select>
            <TextInput value={identityValue} onChange={(e) => setIdentityValue(e.target.value)} placeholder="Nilai identitas" style={{ flex: 1 }} />
            <Button variant="secondary" loading={busy} onClick={() => void handleAddIdentity()}>
              Tambah
            </Button>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
        <GroupLabel>Consent</GroupLabel>
        {CONSENT_TYPES.map((type) => {
          const eff = effectiveConsent(type);
          const granted = eff?.granted ?? false;
          return (
            <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
              <span>{CONSENT_LABEL[type]}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StatusBadge status={granted ? 'GRANTED' : 'OPTED_OUT'} />
                {canUpdateConsent && customer.status === 'ACTIVE' && (
                  <Button variant="secondary" loading={busy} onClick={() => void handleToggleConsent(type, !granted)}>
                    {granted ? 'Opt-out' : 'Opt-in'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {canCreateFeedback && customer.status === 'ACTIVE' && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
          <GroupLabel>Catat Feedback</GroupLabel>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Select value={fbChannel} onChange={(e) => setFbChannel(e.target.value)} style={{ width: 130 }}>
              <option value="WHATSAPP">WhatsApp</option>
              <option value="WALK_IN">Tatap Muka</option>
              <option value="PHONE">Telepon</option>
            </Select>
            <Select value={fbRating} onChange={(e) => setFbRating(e.target.value)} style={{ width: 90 }}>
              {[5, 4, 3, 2, 1].map((r) => (
                <option key={r} value={r}>
                  {r} ★
                </option>
              ))}
            </Select>
          </div>
          <TextInput value={fbComment} onChange={(e) => setFbComment(e.target.value)} placeholder="Komentar (opsional)" style={{ width: '100%', marginBottom: 8 }} />
          <Button variant="secondary" loading={busy} onClick={() => void handleLogFeedback()}>
            Simpan Feedback
          </Button>
        </div>
      )}

      {canCreateComplaint && customer.status === 'ACTIVE' && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
          <GroupLabel>Catat Komplain</GroupLabel>
          <TextInput value={complaintSubject} onChange={(e) => setComplaintSubject(e.target.value)} placeholder="Subjek" style={{ width: '100%', marginBottom: 8 }} />
          <TextInput value={complaintDesc} onChange={(e) => setComplaintDesc(e.target.value)} placeholder="Deskripsi" style={{ width: '100%', marginBottom: 8 }} />
          <Button variant="secondary" loading={busy} onClick={() => void handleLogComplaint()}>
            Simpan Komplain
          </Button>
        </div>
      )}
    </Drawer>
  );
}

function FeedbackTab({
  feedback,
  customerById,
  canUpdate,
  onChanged,
}: {
  feedback: Feedback[];
  customerById: Map<string, Customer>;
  canUpdate: boolean;
  onChanged: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const advance = async (id: string, status: 'REVIEWED' | 'RESOLVED') => {
    setBusyId(id);
    setError(null);
    try {
      await updateFeedbackStatus(id, status);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      {error && <ErrorNote text={error} />}
      {feedback.length === 0 && <EmptyState text="Belum ada feedback." />}
      {feedback.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Pelanggan</th>
              <th style={thStyle}>Kanal</th>
              <th style={thStyle}>Rating</th>
              <th style={thStyle}>Komentar</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {feedback.map((f) => (
              <tr key={f.feedback_id}>
                <td style={tdStyle}>{f.customer_id ? customerById.get(f.customer_id)?.name ?? '—' : '—'}</td>
                <td style={tdStyle}>{f.channel}</td>
                <td style={tdStyle}>{f.rating ?? '—'}</td>
                <td style={tdStyle}>{f.comment ?? '—'}</td>
                <td style={tdStyle}>
                  <StatusBadge status={f.status} />
                </td>
                <td style={tdStyle}>
                  {canUpdate && f.status === 'NEW' && (
                    <Button variant="secondary" loading={busyId === f.feedback_id} onClick={() => void advance(f.feedback_id, 'REVIEWED')}>
                      Tandai Ditinjau
                    </Button>
                  )}
                  {canUpdate && f.status === 'REVIEWED' && (
                    <Button variant="secondary" loading={busyId === f.feedback_id} onClick={() => void advance(f.feedback_id, 'RESOLVED')}>
                      Tandai Selesai
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function ComplaintsTab({
  complaints,
  customerById,
  canUpdate,
  onChanged,
}: {
  complaints: Complaint[];
  customerById: Map<string, Customer>;
  canUpdate: boolean;
  onChanged: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const act = async (id: string, fn: () => Promise<void>) => {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      {error && <ErrorNote text={error} />}
      {complaints.length === 0 && <EmptyState text="Belum ada komplain." />}
      {complaints.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Pelanggan</th>
              <th style={thStyle}>Subjek</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Dibuat</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {complaints.map((c) => (
              <tr key={c.complaint_id}>
                <td style={tdStyle}>{c.customer_id ? customerById.get(c.customer_id)?.name ?? '—' : '—'}</td>
                <td style={tdStyle}>{c.subject}</td>
                <td style={tdStyle}>
                  <StatusBadge status={c.status} />
                </td>
                <td style={tdStyle}>{formatDateTime(c.created_at)}</td>
                <td style={{ ...tdStyle, display: 'flex', gap: 6 }}>
                  {canUpdate && c.status === 'NEW' && (
                    <Button variant="secondary" loading={busyId === c.complaint_id} onClick={() => void act(c.complaint_id, () => updateComplaintStatus(c.complaint_id, 'IN_PROGRESS'))}>
                      Proses
                    </Button>
                  )}
                  {canUpdate && (c.status === 'NEW' || c.status === 'IN_PROGRESS') && (
                    <Button variant="secondary" loading={busyId === c.complaint_id} onClick={() => void act(c.complaint_id, () => updateComplaintStatus(c.complaint_id, 'ESCALATED'))}>
                      Eskalasi
                    </Button>
                  )}
                  {canUpdate && c.status !== 'RESOLVED' && (
                    <Button variant="primary" loading={busyId === c.complaint_id} onClick={() => void act(c.complaint_id, () => resolveComplaint(c.complaint_id))}>
                      Selesaikan
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function MergeReviewTab({
  mergeRequests,
  customerById,
  onChanged,
}: {
  mergeRequests: CustomerMergeRequest[];
  customerById: Map<string, Customer>;
  onChanged: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const decide = async (m: CustomerMergeRequest, approve: boolean, survivorCustomerId: string | null) => {
    setBusyId(m.merge_request_id);
    setError(null);
    try {
      await decideCustomerMerge(m.merge_request_id, approve, survivorCustomerId);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="alex-card" style={{ padding: 12, marginBottom: 16, fontSize: 'var(--alex-fs-body)' }}>
        Kandidat duplikasi TIDAK PERNAH digabung otomatis. Pilih pelanggan mana yang menjadi data utama (survivor) — yang lain akan ditandai sebagai digabung, riwayatnya tetap tersimpan.
      </div>
      {error && <ErrorNote text={error} />}
      {mergeRequests.length === 0 && <EmptyState text="Tidak ada kandidat duplikasi." />}
      {mergeRequests.map((m) => {
        const a = customerById.get(m.customer_a_id);
        const b = customerById.get(m.customer_b_id);
        return (
          <div key={m.merge_request_id} className="alex-card" style={{ padding: 14, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>{a?.name ?? '—'}</strong> vs <strong>{b?.name ?? '—'}</strong>
                <div style={{ color: 'var(--alex-text-secondary)', fontSize: 'var(--alex-fs-body)' }}>
                  Konflik: {m.conflicting_identity_type} = {m.conflicting_identity_value}
                </div>
              </div>
              <StatusBadge status={m.status} />
            </div>
            {m.status === 'PENDING_REVIEW' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <Button variant="primary" loading={busyId === m.merge_request_id} onClick={() => void decide(m, true, m.customer_a_id)}>
                  Gabung — {a?.name ?? 'A'} sebagai utama
                </Button>
                <Button variant="primary" loading={busyId === m.merge_request_id} onClick={() => void decide(m, true, m.customer_b_id)}>
                  Gabung — {b?.name ?? 'B'} sebagai utama
                </Button>
                <Button variant="secondary" loading={busyId === m.merge_request_id} onClick={() => void decide(m, false, null)}>
                  Tolak (tetap terpisah)
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
